import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import styles from './ManagementProfileTab.module.scss';
import useManagementProfileTabLogic from './useManagementProfileTabLogic';
import useDirectorDrawerUrlFlag from './useDirectorDrawerUrlFlag';
import SideDrawer from '../../../../common/_custom/SideDrawer/SideDrawer';
import ManagementUserProfile from '../Overview/components/ManagementProfile/components/ManagementUserProfile';
import { noDataFoundEmptyState, debtProdFilters, PURPLE_CROSS } from '../../../../assets/hostedassets';
import { isAppliedFiltersEmpty } from '../../../../utils/utils';
import greyLock from '../../../../assets/grey-lock-2.svg';
import { DATE_FORMATS, formatDateByFormat } from '../../../../utils/dateUtils';
import { TabModule } from '../../../../utils/constants/commonConstants';
import McaRefreshLoader, { McaDocData } from '../../../../common/McaRefreshLoader/McaRefreshLoader';
import { BureauType } from '../CreditBureau/components/Report/CreditBureauConstants';
import DataTable from '../../../../common/_custom/DataTable/DataTable';
import { DataTableColumnType } from '../../../../common/_custom/DataTable/DataTable.types';
import { hasFilterableColumns } from '../../../../common/_custom/DataTable/utils';
import { ComponentCellRenderer } from '../../../../common/_custom/DataTable/components/CellRenderers/ComponentCellRenderer/ComponentCellRenderer';
import { NumberCellRenderer } from '../../../../common/_custom/DataTable/components/CellRenderers/NumberCellRenderer/NumberCellRenderer';
import { TextInputCellRenderer } from '../../../../common/_custom/DataTable/components/CellRenderers/TextInputCellRenderer/TextInputCellRenderer';
import { SingleSelectDropdownCellRenderer } from '../../../../common/_custom/DataTable/components/CellRenderers/SingleSelectDropdownCellRenderer';
import { MultiSelectDropdownCellRenderer } from '../../../../common/_custom/DataTable/components/CellRenderers/MultiSelectDropdownCellRenderer/MultiSelectDropdownCellRenderer';
import { DatePickerCellRenderer } from '../../../../common/_custom/DataTable/components/CellRenderers/DatePickerCellRenderer/DatePickerCellRenderer';
import Button from '../../../../common/_custom/Button/Button';
import { setCustomFilterDrawer } from '../../../../store/investor/action';
import HistoryDrawer from '../../../../common/HistoryDrawer/HistoryDrawer';
import { LOCK_HEADER_ICON_KEY, MANAGEMENT_PROFILE_FILTER_SOURCE_KEY } from './ManagementProfileTabConstants';
import { MANAGEMENT_PROFILE_FIELD_VALIDATORS } from '../../../../common/ManagementProfile/managementProfileFieldValidators';
import { createManagementProfileComponentRegistry, ManagementProfileStatusPillRenderer } from './managementProfileColumnRenderers';
import { managementProfileValueGetterMap } from './managementProfileValueGetters';
import { Mixpanel } from '../../../../utils/mixpanel';
import { AICA_MANAGEMENT_DIRECTOR_VIEWED } from '../../../../utils/constants/mixpanelEvents/investorEvents';
import AddDirectorModal from './AddDirectorModal/AddDirectorModal';
import { plusWhite } from '../../../../assets/hostedassets';

interface ManagementProfileTabProps {
    navigateToTab?: (tabId: string, additionalParams?: Record<string, string>) => void;
    mcaDocData?: McaDocData | null;
}

function ManagementProfileTab({ navigateToTab, mcaDocData }: ManagementProfileTabProps) {
    const dispatch = useDispatch();
    const userData = useSelector((state: any) => state?.user?.userData);
    const params = useParams();
    const {
        overviewLoading,
        directors,
        totalDirectorsCount,
        columnLocks,
        selectedDirector,
        setSelectedDirector,
        lastUpdatedAt,
        columns,
        pinnedColumns,
        updateCustomFieldStatus,
        updateSingleSelectDropdown,
        updateMultiSelectDropdown,
        onHistoryClick,
        historyDrawerState,
        handleHistoryDrawerClose,
        appliedFilters,
        onFiltersApply,
        refetchDirectors,
        saveDirectorFields,
        isLinkedinRefreshing,
        isDirectorEditingEnabled,
    } = useManagementProfileTabLogic();

    useDirectorDrawerUrlFlag(!!selectedDirector);

    const [addDirectorOpen, setAddDirectorOpen] = useState(false);

    // A modal left open across an org switch (e.g. via URL nav while still mounted) must not
    // submit its half-filled form against the new org — close it so a reopen starts fresh.
    useEffect(() => {
        setAddDirectorOpen(false);
    }, [params?.id]);

    // Mixpanel event only ever carries the PNO/company identifiers, never a
    // per-director one (directorId/din/directorName) — CodeRabbit finding on #3232,
    // this dashboard counts per-PNO module usage, not a per-director breakdown.
    const handleDirectorClick = (row: any) => {
        Mixpanel.track(AICA_MANAGEMENT_DIRECTOR_VIEWED, {
            parentId: userData?.investorOrgId,
            companyOrgId: params?.id,
            websiteUrl: window.location.href,
        });
        setSelectedDirector(row);
    };

    const showFilterButton = useMemo(() => hasFilterableColumns(columns), [columns]);
    const hasActiveFilters = !isAppliedFiltersEmpty(appliedFilters);

    // Filtering is applied by underwriting-engine, so clearing has to go back to the server —
    // `onFiltersApply({})` resets the hook's state AND refetches. The dispatch additionally
    // clears the drawer's own redux copy, so its checkboxes aren't still ticked on reopen.
    const clearFilters = useCallback(() => {
        onFiltersApply({});
        dispatch(setCustomFilterDrawer({ open: false, appliedFilters: { [MANAGEMENT_PROFILE_FILTER_SOURCE_KEY]: {} } }));
    }, [onFiltersApply, dispatch]);

    // `navigateToTab` is a plain function declared in Dashboard's render (index.tsx:433), so it
    // has a new identity every time Dashboard renders. Depending on that identity rebuilt this
    // registry -> customCellRenderers -> useCellRenderers' map on every render, and because that
    // map's VALUES are the cell component types, React unmounted and remounted every <td>
    // rather than updating it. Hovering then self-sustained: the remount destroyed the element
    // under the cursor, the browser fired mouseenter on its replacement, that tooltip set state,
    // repeat — measured at ~55 cell remounts/second. It closed any open dropdown (StatusDropdown
    // lost its isOpen state) and reset the hover-revealed history icon. Hold the callback in a
    // ref and depend on a stable wrapper instead.
    const navigateToTabRef = useRef(navigateToTab);
    navigateToTabRef.current = navigateToTab;
    const stableNavigateToTab = useCallback(
        (tabId: string, additionalParams?: Record<string, string>) => navigateToTabRef.current?.(tabId, additionalParams),
        [],
    );

    const componentRenderFnRegistry = useMemo(
        () => createManagementProfileComponentRegistry({ columnLocks, navigateToTab: stableNavigateToTab }),
        [columnLocks, stableNavigateToTab],
    );

    const customCellRenderers = useMemo(
        () => ({
            [DataTableColumnType.COMPONENT]: (props: any) => (
                <ComponentCellRenderer {...props} dynamicProps={{ componentRenderFnRegistry }} />
            ),
            [DataTableColumnType.PILL]: (props: any) => <ManagementProfileStatusPillRenderer {...props} />,
            [DataTableColumnType.TEXT]: (props: any) => (
                <TextInputCellRenderer
                    {...props}
                    dynamicProps={{
                        updateCustomFieldStatus,
                        isDisabled: !!props?.column?.disable,
                        validate: MANAGEMENT_PROFILE_FIELD_VALIDATORS[props?.column?.field],
                        truncate: true,
                    }}
                />
            ),
            [DataTableColumnType.NUMBER]: (props: any) => (
                <NumberCellRenderer {...props} dynamicProps={{ updateCustomFieldStatus, isDisabled: !!props?.column?.disable }} />
            ),
            [DataTableColumnType.SINGLE_SELECT_DROPDOWN]: (props: any) => (
                <SingleSelectDropdownCellRenderer
                    {...props}
                    dynamicProps={{ updateSingleSelectDropdown, isDisabled: !!props?.column?.disable }}
                />
            ),
            [DataTableColumnType.MULTI_SELECT_DROPDOWN]: (props: any) => (
                <MultiSelectDropdownCellRenderer
                    {...props}
                    dynamicProps={{ updateMultiSelectDropdown, isDisabled: !!props?.column?.disable }}
                />
            ),
            [DataTableColumnType.DATE_PICKER]: (props: any) => (
                <DatePickerCellRenderer {...props} dynamicProps={{ updateCustomFieldStatus, isDisabled: !!props?.column?.disable }} />
            ),
        }),
        [componentRenderFnRegistry, updateCustomFieldStatus, updateSingleSelectDropdown, updateMultiSelectDropdown],
    );

    return (
        <div className={styles.ManagementProfileTab}>
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <h2 className={styles.heading}>Management Profile</h2>
                </div>
                <div className={styles.headerRight}>
                    <McaRefreshLoader mcaDocData={mcaDocData} />
                    {lastUpdatedAt && (
                        <span className={styles.lastUpdated}>
                            Last updated on {formatDateByFormat(lastUpdatedAt, DATE_FORMATS.DATE_FORMAT_DD_MMM_YYYY)}
                        </span>
                    )}
                    <div className={styles.dataSourceBadge}>
                        <span className={styles.dataSourceBadgeLabel}>Source:</span>
                        <span className={styles.dataSourceBadgeValue}>MCA</span>
                    </div>
                    {/* Columns are config-driven, so a PNO whose ViewConfig seeds no filterable
                        column (and the fallback columns, which have none) would otherwise open an
                        empty drawer. */}
                    {showFilterButton && (
                        <Button
                            text=""
                            icon={debtProdFilters}
                            variant="white"
                            aria-label="Filters"
                            onClick={() => dispatch(setCustomFilterDrawer({ open: true }))}
                            className={`${styles.filterButton} ${hasActiveFilters ? styles.filterButtonActive : ''}`}
                            // Same active-filter affordance as the Deals and Leads tables: the
                            // button goes purple and grows a cross that clears in one click.
                            endIcon={hasActiveFilters ? PURPLE_CROSS : null}
                            endIconStyles={{ height: '0.8rem', marginLeft: '0.25rem' }}
                            endIconClick={clearFilters}
                        />
                    )}
                    <Button
                        text="Add"
                        icon={plusWhite}
                        variant="primary"
                        aria-label="Add Director"
                        onClick={() => setAddDirectorOpen(true)}
                        className={styles.filterButton}
                    />
                </div>
            </div>
            <div className={styles.body}>
                <div className={styles.content}>
                    <DataTable
                        columns={columns}
                        rows={directors}
                        totalRowCount={totalDirectorsCount}
                        loading={overviewLoading}
                        emptyState={
                            <div className={styles.emptyState}>
                                <img src={noDataFoundEmptyState} alt="" className={styles.emptyStateIcon} />
                                <div className={styles.emptyStateTitle}>No data available</div>
                            </div>
                        }
                        noSearchResults={<div className={styles.emptyStateTitle}>No results found</div>}
                        customCellRenderers={customCellRenderers}
                        valueGetterMap={managementProfileValueGetterMap}
                        pinnedColumns={pinnedColumns}
                        onRowClick={(_e, row) => handleDirectorClick(row)}
                        onHistoryClick={onHistoryClick}
                        headerIconMap={{ [LOCK_HEADER_ICON_KEY]: <img src={greyLock} alt="locked" /> }}
                        filterSourceKey={MANAGEMENT_PROFILE_FILTER_SOURCE_KEY}
                        onFiltersApply={onFiltersApply}
                    />
                </div>
            </div>
            {selectedDirector && (
                <SideDrawer onClose={() => setSelectedDirector(null)} width={'60%'}>
                    <ManagementUserProfile
                        showUserProfile={selectedDirector}
                        dataPreloaded
                        editable={isDirectorEditingEnabled}
                        linkedinRefreshing={isLinkedinRefreshing}
                        onEditSave={(updates) => saveDirectorFields(selectedDirector.managementProfileId ?? '', updates)}
                        redirectToCreditBureau={() => {
                            const director = selectedDirector;
                            setSelectedDirector(null);
                            navigateToTab?.(TabModule.CREDIT_BUREAU, {
                                bureauType: BureauType.ALL,
                                directorName: director?.name ?? '',
                                focusDirectorId: director?.directorId ?? '',
                            });
                        }}
                        redirectToCompliances={() => {
                            const director = selectedDirector;
                            setSelectedDirector(null);
                            navigateToTab?.(TabModule.LEGAL_CHECKS, {
                                directorName: director?.name ?? '',
                                directorDin: director?.din ?? '',
                            });
                        }}
                        regulatoryChecksLocked={columnLocks?.regulatoryChecks}
                        redirectToRegulatoryChecks={() => {
                            const director = selectedDirector;
                            setSelectedDirector(null);
                            navigateToTab?.(TabModule.LEGAL_CHECKS, {
                                directorName: director?.name ?? '',
                                directorDin: director?.din ?? '',
                                section: 'regulatoryChecks',
                            });
                        }}
                        redirectToRiskIndicators={() => {
                            const director = selectedDirector;
                            setSelectedDirector(null);
                            navigateToTab?.(TabModule.RELATED_PARTY, {
                                directorName: director?.name ?? '',
                                directorDin: director?.din ?? '',
                            });
                        }}
                    />
                </SideDrawer>
            )}
            <HistoryDrawer
                open={historyDrawerState.open}
                onClose={handleHistoryDrawerClose}
                referenceId={historyDrawerState.referenceId}
                fieldKey={historyDrawerState.fieldKey}
                title={historyDrawerState.title}
                assesseeOrgId={historyDrawerState.assesseeOrgId}
                historySource={historyDrawerState.historySource}
            />
            <AddDirectorModal
                open={addDirectorOpen}
                onClose={() => setAddDirectorOpen(false)}
                onSuccess={() => {
                    setAddDirectorOpen(false);
                    refetchDirectors();
                }}
                organizationId={params?.id}
            />
        </div>
    );
}

export default ManagementProfileTab;
